<template>
    <Dialog v-model:visible="props.visible" modal position="center" :closable="false" :draggable="false"
        class="send-email-dialog" :style="{ width: '94vw', maxWidth: '460px' }"
        :contentStyle="{ overflowX: 'hidden' }"
        @click.right="clickActionPreventDefault" @click.middle="clickActionPreventDefault"
        @touchstart="touchActionPreventDefault" @copy="clickActionPreventDefault" @dblclick="clickActionPreventDefault">
        <template #header>
            <div class="send-email-header">
                <div class="border-circle inline-flex justify-content-center align-items-center h-6rem w-6rem -mt-8">
                    <img alt="MidOpt logo" class="logo" src="@/assets/logo.png" width="100" height="100" />
                </div>
                <Button icon="pi pi-times" text rounded aria-label="Close" class="send-email-close"
                    @click="emit('close')" />
            </div>
        </template>

        <form @submit="onSubmit">
            <div v-for="field in formFields" :key="field.title" class="flex flex-column align-items-center">
                <div v-if="field.visible"
                    :class="{ 'flex flex-column gap-2 w-10 text-base': true, 'hidden-field': field.hidden }">
                    <label :for="field.title">{{ field.label }}<small v-if="field.required"
                            class="text-red-700 p-2 text-xl">*</small></label>
                    <InputText :id="field.title" v-model="formData[field.title]" :invalid="!!formErrors[field.title]"
                        :aria-describedby="`${field.title}-help`" class="text-base py-4 my-2" />
                    <small :id="`${field.title}-help`" class="text-red-600">{{ formErrors[field.title] }}</small>
                </div>
            </div>
            <div v-if="isFieldVisible('owner') || isFieldVisible('badge')"
                class="flex flex-column align-items-center">
                <div class="flex flex-column sm:flex-row gap-3 w-10 text-base sm:align-items-end">
                    <div v-if="isFieldVisible('owner')" class="flex flex-column gap-2 flex-1" style="min-width: 0">
                        <label for="owner">Owner</label>
                        <Dropdown v-model="formData['owner']" :options="owners" placeholder="Select an Owner"
                            class="w-full" />
                        <small :id="`owner-help`" class="text-red-600">{{ formErrors['owner'] }}</small>
                    </div>
                    <div v-if="isFieldVisible('badge')" class="flex mb-2">
                        <FileUpload customUpload ref="fileUpload" mode="basic" class="send-email-upload"
                            name="badge" accept="image/*" @select="onFileUpload" chooseLabel="Image"
                            :disabled="ocrProcessing">
                            <template #uploadicon>
                                <i class="pi pi-upload mr-2"></i>
                            </template>
                            <template #chooseicon>
                                <i v-show="!ocrProcessing" class="pi pi-upload mr-2"></i>
                                <i v-show="ocrProcessing" class="pi pi-spinner pi-spin mr-2"></i>
                            </template>
                        </FileUpload>
                    </div>
                </div>
            </div>
            <div class="flex justify-content-center gap-4 mt-6">
                <Button type="submit" label="Send" class="w-10rem text-base py-3" :loading="props.processing" />
            </div>
        </form>
    </Dialog>
</template>

<script setup>
import * as yup from 'yup';
import { useForm, useField } from 'vee-validate';
import { reactive, defineEmits, onMounted, ref, watch } from "vue";
import { clickActionPreventDefault, touchActionPreventDefault } from '@/services/screen';
import QueueService from '@/services/queue.js'

const owners = process.env.VUE_APP_OWNERS_LIST.split(',')
const formFields = reactive([
    { title: 'name', label: 'Name', required: true, visible: true },
    { title: 'email', label: 'Email', required: false, visible: true },
    { title: 'company', label: 'Company', required: true, visible: true },
    { title: 'job', label: 'Job Title', required: false, visible: true },
    { title: 'phone', label: 'Phone', required: false, visible: true },
    { title: 'address', label: 'Address', required: false, visible: true, hidden: true },
    { title: 'owner', label: 'Owner', required: true, visible: false },
    { title: 'badge', label: 'Image', required: false, visible: false },
    { title: 'notes', label: 'Notes', required: false, visible: true },
    { title: 'demographics', label: 'Demographics', required: false, visible: true, hidden: true },
    { title: 'queueData', label: 'Queue Data', required: false, visible: false },
]);
const formData = ref({});
const formErrors = ref({});
const fileUpload = ref(null);
const ocrProcessing = ref(false);
const autoSubmitted = ref(false);

const form = useForm();
const emit = defineEmits(['close', 'submit']);

const props = defineProps({
    visible: {
        type: Boolean,
        default: () => false
    },
    processing: {
        type: Boolean,
        default: () => false
    },
    formDefaultData: {
        type: Object,
        default: () => { }
    },
    autoSubmit: {
        type: Boolean,
        default: () => false
    },
    hiddenFields: {
        type: Array,
        default: () => []
    }
});

onMounted(() => initializeForm());

// Reset form data and errors when dialog is closed
watch(() => props.visible, (visible) => {
    if (visible) {
        handleReset();
        autoSubmitted.value = false;
    }
});

// Fill out form data when formDefaultData changes
watch(() => props.formDefaultData, (data) => {
    if (!data || autoSubmitted.value) return;

    fillOutForm(data);

    if (props.autoSubmit) {
        onSubmit();
        setTimeout(() => {
            autoSubmitted.value = false;
        }, 1000);
        autoSubmitted.value = true;
    }
});

const isFieldVisible = (field) => {
    if (props.hiddenFields.includes(field)) return false;
    return true;
}

const { handleSubmit, handleReset } = useForm({
    validationSchema: yup.object({
        name: yup.string().required(),
        email: yup.string().email(),
        company: yup.string().required(),
        phone: yup.string(),
        notes: yup.string(),
        queueData: yup.object(),
    }),
});

// Initialize form data and errors
const initializeForm = () => {
    formFields.forEach(f => {
        const field = useField(() => f.title);
        // Set initial value
        formData.value[f.title] = field.value;
        // Set initial error
        formErrors.value[f.title] = field.errorMessage;
    });
};

const fillOutForm = (data) => {
    formFields.forEach(f => {
        if (!data[f.title]) return;
        formData.value[f.title] = data[f.title];
    });
};

// Handle form submission
const onSubmit = handleSubmit(values => {
    emit('submit', formData.value);
});

const onFileUpload = async (event) => {
    if (event.files.length) {
        ocrProcessing.value = true;
        const file = event.files[0];

        const form = new FormData();
        form.append('file', file);

        await QueueService.parseBadge(form)
            .then(({ data }) => {
                fillOutForm({
                    name: `${data.first_name} ${data.last_name}`,
                    company: data.company_name,
                });
            })
            .catch(error => {
                console.log(error)
            });

        ocrProcessing.value = false;
        fileUpload.value.clear();
    }
};
</script>

<style scoped>
.send-email-btn {
    background-color: #ffc34e;
    border-color: #ffc34e;
}

.send-email-btn>.pi-envelope:before {
    font-size: 2.5rem !important;
    font-weight: 900;
}

.send-email-btn,
.clear-all-btn {
    color: #000000 !important;
}

.hidden-field {
    display: none !important;
}

/* Header: centered floating logo with the close button pinned top-right. */
.send-email-header {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
}

.send-email-close {
    position: absolute;
    top: 0;
    right: 0;
}

/* Keep the form from ever pushing the dialog into a horizontal scroll. */
form {
    max-width: 100%;
}

/* On phones the owner/badge row stacks, so the upload spans full width. */
@media (max-width: 576px) {

    .send-email-upload,
    .send-email-upload :deep(.p-button) {
        width: 100%;
    }
}
</style>